import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api.js";
import Landing from "./Landing.jsx";
import KeyLogin from "./KeyLogin.jsx";
import Crash from "./Crash.jsx";
import Company from "./company/Company.jsx";
import TopBar from "./TopBar.jsx";
import { InvitePage } from "./company/partners/Trust.jsx";
import Account from "./Account.jsx";
import Activate from "./Activate.jsx";
import Admin from "./Admin.jsx";

// Маршрути в hash: #/ — компанії, #/c/<id>[/<розділ>] — компанія
function useRoute() {
  const [hash, setHash] = useState(location.hash);
  useEffect(() => {
    const on = () => setHash(location.hash);
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  // Запит «обмін статусом СМКОР» за посиланням: #/invite/<токен>
  const inv = hash.match(/^#\/invite\/([A-Za-z0-9_-]{20,64})$/);
  if (inv) return { page: "invite", token: inv[1] };
  // Кабінет користувача: #/account/<розділ>
  const acc = hash.match(/^#\/account(?:\/([a-z]+))?$/);
  if (acc) return { page: "account", tab: acc[1] || "profile" };
  // Адмінка (лише адміністраторам): #/admin/<розділ>; активація акаунта: #/activate
  const adm = hash.match(/^#\/admin(?:\/([a-z]+))?$/);
  if (adm) return { page: "admin", tab: adm[1] || "users" };
  if (hash === "#/activate") return { page: "activate" };
  const m = hash.match(/^#\/c\/([0-9a-f-]{36})(?:\/([a-z]+))?(?:\/(.+))?/);
  return m ? { page: "company", id: m[1], section: m[2] || "overview", sub: m[3] ? decodeURIComponent(m[3]) : null } : { page: "companies" };
}

export default function App() {
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    api("/api/auth/me").then((r) => setUser(r.user)).catch(() => setUser(null));
  }, []);
  const route = useRoute();
  // Бічне меню розділів на вузьких екранах — висувна панель; закривається переходом і Escape
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [route.page, route.id, route.section, route.sub]);
  if (user === undefined) return null;
  if (!user) return <Landing onLogin={setUser} />;
  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    setUser(null);
  };
  return (
    <>
      <TopBar user={user} route={route} onLogout={logout} navOpen={navOpen} onNavToggle={() => setNavOpen((v) => !v)} />
      {/* key — новий екземпляр на кожну компанію: запізнілі відповіді попередньої не потрапляють у шапку нової */}
      {/* Без активації — лише екран активації (і профіль); адмінка — адміністраторам */}
      {!user.activated && route.page !== "account" ? <main className="shell"><Crash><Activate user={user} onActivated={() => api("/api/auth/me").then((r) => setUser(r.user))} /></Crash></main>
        : route.page === "admin" && user.isAdmin ? <main className="shell"><Crash><Admin user={user} tab={route.tab} /></Crash></main>
        : route.page === "activate" ? <main className="shell"><Crash><Activate user={user} onActivated={() => api("/api/auth/me").then((r) => setUser(r.user))} /></Crash></main>
        : route.page === "company"
        ? <main className="cab-shell"><Crash><Company key={route.id} id={route.id} section={route.section} sub={route.sub} navOpen={navOpen} onNavClose={() => setNavOpen(false)} /></Crash></main>
        : route.page === "account" ? <main className="shell"><Crash><Account user={user} tab={route.tab} onUser={setUser} /></Crash></main>
        : route.page === "invite" ? <main className="shell"><Crash><InvitePage token={route.token} /></Crash></main>
        : <main className="shell"><Crash><Companies /></Crash></main>}
      <footer className="app-footer"><div className="wrap">eCabinet — незалежний сервіс, не є офіційним ресурсом Державної податкової служби України.</div></footer>
    </>
  );
}

// Компанії користувача; нова — входом ключем КЕП підприємства (#/add відкриває форму одразу, напр. з меню)
function Companies() {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(() => location.hash === "#/add");
  const reload = useCallback(() => api("/api/companies").then((r) => setList(r.companies)), []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const on = () => { if (location.hash === "#/add") setAdding(true); };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  // Після закриття фокус — на кнопку, якою форму відкривали (вона з'являється лише після рендеру)
  const addBtn = useRef(null);
  const close = (refocus) => {
    setAdding(false);
    if (location.hash === "#/add") history.replaceState(null, "", "#/");
    if (refocus) requestAnimationFrame(() => addBtn.current?.focus());
  };
  const form = (
    <div onKeyDown={(e) => { if (e.key === "Escape") close(true); }}>
      <KeyLogin autoFocus onDone={(r) => { close(); reload(); location.hash = `#/c/${r.company.id}`; }} />
    </div>
  );
  // Додавання компанії, коли вже є інші: лише форма входу ключем по центру, без заголовка й списку
  if (adding && list?.length > 0) return (
    <section className="add-only">
      <h1 className="sr-only">Додати компанію</h1>
      {form}
      <button type="button" className="link-btn add-only-back" onClick={() => close(false)}>Повернутися до компаній</button>
    </section>
  );
  // Перша компанія: одна дія — вхід ключем; по центру, без картки в картці й без «Скасувати»
  if (list?.length === 0) return (
    <section className="add-only first-co">
      <div className="first-co-head">
        <h1>Додайте першу компанію</h1>
        <p>Увійдіть ключем КЕП компанії — eCabinet отримає її дані з кабінету ДПС, а ви станете власником.</p>
      </div>
      {form}
      <p className="muted small first-co-note">Компанію вже підключив колега? Увійдіть ключем цієї компанії — і отримаєте до неї доступ.</p>
    </section>
  );
  return (
    <section>
      <div className="row">
        <h1>Компанії</h1>
        {list?.length > 0 && <button type="button" className={adding ? "btn btn-secondary" : "btn"} ref={addBtn} onClick={() => (adding ? close(true) : setAdding(true))}>{adding ? "Скасувати" : "+ Додати компанію"}</button>}
      </div>
      {list === null ? <p className="muted">Завантаження…</p> : (
        <>
          <ul className="list">
            {list.map((c) => (
              <li key={c.id}><a href={`#/c/${c.id}`}><b>{c.name || c.edrpou}</b><span className="muted">{c.edrpou} · {roleName[c.role]}</span></a></li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

const roleName = { viewer: "перегляд", preparer: "підготовка", submitter: "подання", owner: "власник" };
